import type { NamePath } from 'antd/es/form/interface';
import dayjs from 'dayjs';

import type {
  InventoryExperiences,
  InventoryItem,
  InventoryPacks,
  InventoryRooms,
  ModifiedData,
} from './InventoryStore';
import type { ComputePacksResult } from './utils/packs';

export const getModifiedValues = (
  currentFormData:
    InventoryExperiences | InventoryRooms | InventoryPacks | undefined,
  initialValues: ModifiedData | undefined
): ModifiedData => {
  if (!currentFormData || !initialValues) {
    return (currentFormData as ModifiedData) || {};
  }

  const result: ModifiedData = {};

  Object.entries(currentFormData).forEach(([dateKey, experiences]) => {
    if (typeof experiences !== 'object' || !experiences) {
      return;
    }

    Object.entries(experiences).forEach(([itemId, currentValues]) => {
      if (typeof currentValues !== 'object' || !currentValues) {
        return;
      }

      const initialItemValues = initialValues[dateKey]?.[itemId];

      const changedFields: InventoryItem = {};

      Object.entries(currentValues).forEach(([field, currentValue]) => {
        const initialValue = initialItemValues?.[field];

        if (currentValue !== initialValue) {
          changedFields[field] = currentValue;
        }
      });

      if (Object.keys(changedFields).length > 0) {
        if (!result[dateKey]) {
          result[dateKey] = {};
        }

        result[dateKey][itemId] = changedFields;
      }
    });
  });

  return result;
};

export const retrieveDataFromNamePath = (name: NamePath | undefined) => {
  if (!name || !Array.isArray(name)) {
    return;
  }

  const [dateKey, itemId, field] = name as [
    string,
    string | number,
    string | number,
  ];

  return { dateKey, itemId, field };
};

export const retrieveFormValues = (
  inventory: ComputePacksResult | Partial<ComputePacksResult>
) => {
  const inventoryExperiences: InventoryExperiences = {};
  const inventoryRooms: InventoryRooms = {};
  const inventoryPacks: InventoryPacks = {};

  Object.keys(inventory).forEach((date) => {
    const entry = inventory[date];

    inventoryExperiences[date] = entry?.experiences ?? {};
    inventoryRooms[date] = entry?.rooms ?? {};
    inventoryPacks[date] = entry?.packs ?? {};
  });

  return {
    inventoryExperiences,
    inventoryRooms,
    inventoryPacks,
  };
};

/**
 * Computes the minimal set of form field paths that changed between two
 * inventory entries for a given date/entity. Use this to call form.setFields()
 * instead of form.setFieldsValue(wholeInventory), which would notify every
 * Form.Item subscriber regardless of whether its value changed.
 */
export const getChangedFormFields = (
  prevEntry: Record<string, unknown> | undefined,
  newEntry: Record<string, unknown> | undefined,
  dateKey: string,
  itemId: string | number
): { name: (string | number)[]; value: unknown }[] => {
  const prev = prevEntry ?? {};
  const next = newEntry ?? {};
  const allKeys = new Set([...Object.keys(prev), ...Object.keys(next)]);
  const changedFields: { name: (string | number)[]; value: unknown }[] = [];

  allKeys.forEach((field) => {
    if (prev[field] !== next[field]) {
      changedFields.push({
        name: [dateKey, String(itemId), field],
        value: next[field],
      });
    }
  });

  return changedFields;
};

export const mergeInventoryObjects = <T extends Record<string, unknown>>(
  target: T,
  source: T
): T => {
  const result = { ...target };

  Object.entries(source).forEach(([dateKey, dateEntry]) => {
    if (!dateEntry || typeof dateEntry !== 'object') {
      (result as Record<string, unknown>)[dateKey] = dateEntry;

      return;
    }

    const targetDateEntry = (result[dateKey] as Record<string, unknown>) || {};

    Object.entries(dateEntry as Record<string, unknown>).forEach(
      ([category, categoryData]) => {
        if (!categoryData || typeof categoryData !== 'object') {
          targetDateEntry[category] = categoryData;

          return;
        }

        const targetCategory =
          (targetDateEntry[category] as Record<string, unknown>) || {};

        Object.entries(categoryData as Record<string, unknown>).forEach(
          ([itemId, itemData]) => {
            if (!itemData || typeof itemData !== 'object') {
              targetCategory[itemId] = itemData;

              return;
            }

            targetCategory[itemId] = {
              ...(targetCategory[itemId] as Record<string, unknown>),
              ...(itemData as Record<string, unknown>),
            };
          }
        );

        targetDateEntry[category] = targetCategory;
      }
    );

    (result as Record<string, unknown>)[dateKey] = targetDateEntry;
  });

  return result;
};

const deepEqual = <T>(a: T, b: T): boolean => {
  if (a === b) {
    return true;
  }

  if (a === null || a === undefined || b === null || b === undefined) {
    return false;
  }

  if (typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }

  const keysA = Object.keys(a as Record<string, unknown>);
  const keysB = Object.keys(b as Record<string, unknown>);

  if (keysA.length !== keysB.length) {
    return false;
  }

  return keysA.every(
    (key) =>
      keysB.includes(key) &&
      deepEqual(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key]
      )
  );
};

type DateEntry = ComputePacksResult[string];
type Category = keyof DateEntry;

export const findInventoryDifferences = (
  original: ComputePacksResult,
  modified: ComputePacksResult
): Partial<ComputePacksResult> => {
  const differences: Partial<ComputePacksResult> = {};

  const allDateKeys = new Set([
    ...Object.keys(modified),
    ...Object.keys(original),
  ]);

  allDateKeys.forEach((dateKey) => {
    const modifiedDateEntry = modified[dateKey];
    const originalDateEntry = original[dateKey];

    if (!(dateKey in modified)) {
      differences[dateKey] = originalDateEntry;

      return;
    }

    if (!modifiedDateEntry || typeof modifiedDateEntry !== 'object') {
      if (!deepEqual(originalDateEntry, modifiedDateEntry)) {
        differences[dateKey] = modifiedDateEntry;
      }

      return;
    }

    const dateEntryDiff = {} as Partial<DateEntry>;
    let hasDifferencesInDate = false;

    const allCategories = new Set<string>([
      ...Object.keys(modifiedDateEntry),
      ...(originalDateEntry && typeof originalDateEntry === 'object'
        ? Object.keys(originalDateEntry)
        : []),
    ]);

    allCategories.forEach((category) => {
      const cat = category as Category;
      const modifiedCategoryData = modifiedDateEntry[cat];
      const originalCategoryData = originalDateEntry?.[cat];

      if (!(category in modifiedDateEntry)) {
        (dateEntryDiff as Record<string, unknown>)[category] =
          originalCategoryData;
        hasDifferencesInDate = true;

        return;
      }

      if (!modifiedCategoryData || typeof modifiedCategoryData !== 'object') {
        if (!deepEqual(originalCategoryData, modifiedCategoryData)) {
          (dateEntryDiff as Record<string, unknown>)[category] =
            modifiedCategoryData;
          hasDifferencesInDate = true;
        }

        return;
      }

      const categoryDiff: Record<number, unknown> = {};
      let hasDifferencesInCategory = false;

      const allItemIds = new Set([
        ...Object.keys(modifiedCategoryData),
        ...(originalCategoryData && typeof originalCategoryData === 'object'
          ? Object.keys(originalCategoryData)
          : []),
      ]);

      allItemIds.forEach((itemId) => {
        const modifiedItem =
          modifiedCategoryData[
            itemId as unknown as keyof typeof modifiedCategoryData
          ];
        const originalItem = originalCategoryData
          ? originalCategoryData[
              itemId as unknown as keyof typeof originalCategoryData
            ]
          : undefined;

        if (!(itemId in modifiedCategoryData)) {
          categoryDiff[Number(itemId)] = originalItem;
          hasDifferencesInCategory = true;

          return;
        }

        if (
          !modifiedItem ||
          typeof modifiedItem !== 'object' ||
          !deepEqual(originalItem, modifiedItem)
        ) {
          categoryDiff[Number(itemId)] = modifiedItem;
          hasDifferencesInCategory = true;
        }
      });

      if (hasDifferencesInCategory) {
        (dateEntryDiff as Record<string, unknown>)[category] = categoryDiff;
        hasDifferencesInDate = true;
      }
    });

    if (hasDifferencesInDate) {
      differences[dateKey] = dateEntryDiff as DateEntry;
    }
  });

  return differences;
};

export const isTimePeriodDisabled = (
  startDate: Date | string,
  endOfSaleDate: string
): boolean =>
  dayjs(startDate).startOf('day').isBefore(dayjs().startOf('day')) ||
  dayjs(startDate).startOf('day').isAfter(dayjs(endOfSaleDate).startOf('day'));

export const isDatePast = (date: Date | string): boolean =>
  dayjs(date).startOf('day').isBefore(dayjs().startOf('day'));

export const isDateAfterEndOfSale = (
  date: Date | string,
  endOfSaleDate: string
): boolean =>
  dayjs(date).startOf('day').isAfter(dayjs(endOfSaleDate).startOf('day'));
